'use client';

import {
  fetchPointsMallAllProducts,
  fetchPointsMallCouponList,
  fetchPointsMallProducts,
} from '@/api/account';
import { IPointsMallCouponItemWithPoint, IRedeemableItem } from '@/api/types/account';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import ProductRedeemCard from '@/components/Product/ProductRedeemCard';
import SkeletonProductCard from '@/components/Product/SkeletonProductCard';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import SortMenu from '@/components/SortMenu';
import Tabs from '@/components/Tabs';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';

import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useSearchParams } from 'next/navigation';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import CouponRedeemCard from './CouponRedeemCard';

interface RedeemableItemsListProps {
  className?: string;
  onRedeemableItemClick?: (item: IRedeemableItem | IPointsMallCouponItemWithPoint) => void;
  initialItems?: Array<IRedeemableItem | IPointsMallCouponItemWithPoint>;
  initialTotal?: number;
  pageLimit?: number;
}

const RedeemableItemsList = ({
  className = '',
  onRedeemableItemClick = () => {},
  initialItems = [],
  initialTotal = 0,
  pageLimit,
}: RedeemableItemsListProps) => {
  const t = useTranslations('account.pointsMall.redeemableItemsList');
  const [serverList, setServerList] =
    useState<Array<IRedeemableItem | IPointsMallCouponItemWithPoint>>(initialItems);
  const { deviceType } = useContext(ResponsiveContext);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(initialTotal);
  const [listLoading, setListLoading] = useState(false);
  const [redeemableList, setRedeemableList] =
    useState<Array<IRedeemableItem | IPointsMallCouponItemWithPoint>>(initialItems);
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('xl', '2xl'));
  const [activeTab, setActiveTab] = useState<string>('0');
  const [sortValue, setSortValue] = useState<string>('default');
  const currency = useStore((state) => state.currency);
  const { trackEvent } = useReporting();
  // 手动点击切换tab
  const [handleChangeTab, setHandleChangeTab] = useState<boolean>(false);
  // 手动点击切换排序
  const [handleChangeSort, setHandleChangeSort] = useState<boolean>(false);

  const searchParams = useSearchParams();
  const tabItems = [
    {
      key: '0',
      label: t('tabs.all'),
    },
    {
      key: '1',
      label: t('tabs.frame'),
    },
    {
      key: '2',
      label: t('tabs.accessories'),
    },
    {
      key: '3',
      label: t('tabs.coupons'),
    },
  ];

  const sortOptions = [
    {
      label: t('sort.default'),
      value: 'default',
    },
    {
      label: t('sort.pointsLowToHigh'),
      value: 'point_asc',
    },
    {
      label: t('sort.pointsHighToLow'),
      value: 'point_desc',
    },
  ];
  const getRedeemableList = async () => {
    setListLoading(true);
    try {
      if (deviceType === EPlatform.WEB) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
      if (activeTab === '0') {
        // 全部
        const { code, data } = await fetchPointsMallAllProducts({
          order: sortValue,
          page: page.toString(),
          limit: pageLimit.toString(),
        });
        if (code === 200) {
          if (deviceType === EPlatform.WEB) {
            setRedeemableList(data.list as any);
          } else {
            setRedeemableList((prevList) => [...prevList, ...data.list] as any);
          }
          setTotal(data.total);
        }
      }
      if (activeTab === '3') {
        // 优惠券
        const { code, data } = await fetchPointsMallCouponList({
          page,
          limit: pageLimit,
          currency: currency.name,
          order: sortValue,
        });
        if (code === 200) {
          if (deviceType === EPlatform.WEB) {
            setRedeemableList(data.list as any);
          } else {
            setRedeemableList((prevList) => [...prevList, ...data.list] as any);
          }
          setTotal(data.total);
        }
      }
      if (['1', '2'].includes(activeTab)) {
        // 眼镜或饰品
        const { code, data } = await fetchPointsMallProducts({
          page: page.toString(),
          limit: pageLimit.toString(),
          currency: currency.name,
          order: sortValue,
          ...(['1', '2'].includes(activeTab) && { 'filter[is_glass]': activeTab }),
        });

        if (code === 200) {
          if (deviceType === EPlatform.WEB) {
            setRedeemableList(data.list as any);
          } else {
            setRedeemableList((prevList) => [...prevList, ...data.list] as any);
          }
          setTotal(data.total);
        }
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setListLoading(false);
    }
  };
  const sendTrack = (
    index: number,
    product: IRedeemableItem & {
      business_sku: string;
      sku: string;
      products_category: string;
    },
    sdEventName: string,
  ) => {
    const sdEventParams = {
      product_index: index,
      business_sku: product.business_sku,
      shop_sku: product.shop_sku,
      product_sku: product.sku,
      product_id: `${product.id}`,
      product_name: product.name,
      product_class_name: product?.products_category,
    };

    trackEvent(
      {
        eventName: sdEventName,
        properties: sdEventParams,
      },
      [ProviderType.SENSORS],
    );
  };

  const sendTrackCoupon = (
    index: number,
    couponInfo: IPointsMallCouponItemWithPoint,
    sdEventName: string,
  ) => {
    const sdEventParams = {
      coupon_index: index,
      coupon_id: `${couponInfo.id}`,
      coupon_name: couponInfo.name,
    };

    trackEvent(
      {
        eventName: sdEventName,
        properties: sdEventParams,
      },
      [ProviderType.SENSORS],
    );
  };
  // 渲染产品列表
  const renderProductList = () => {
    if (listLoading && deviceType === EPlatform.WEB) {
      return (
        <>
          <SkeletonProductCard />
          <SkeletonProductCard />
          <SkeletonProductCard />
          {!isMediumScreen && <SkeletonProductCard />}
        </>
      );
    }
    if (redeemableList.length > 0) {
      return redeemableList.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          {activeTab === '3' || (item as any).point_morph_category === '2' ? (
            <CouponRedeemCard
              couponInfo={item as IPointsMallCouponItemWithPoint}
              onRedeemClick={(_index, product) => {
                onRedeemableItemClick?.({ ...item, point_morph_category: '2' });
                sendTrackCoupon(_index, product as any, 'PointsMallCouponMkt_Click');
              }}
              index={index}
              onInView={(_index, product) => {
                sendTrackCoupon(_index, product as any, 'PointsMallCouponMkt_Exp');
              }}
              sendTrackCoupon={(_index, product, sdEventName) => {
                sendTrackCoupon(_index, product as any, sdEventName);
              }}
            />
          ) : (
            <ProductRedeemCard
              className="w-full"
              isPointsMall
              index={index}
              product={item as IRedeemableItem}
              handelClick={(_index, product) => {
                onRedeemableItemClick?.({ ...item, point_morph_category: '1' });
                sendTrack(_index, product as any, 'PointsMallProductMkt_Click');
              }}
              onInView={(_index, product) => {
                sendTrack(_index, product as any, 'PointsMallProductMkt_Exp');
              }}
              onValidExposure={(_index, product) => {
                sendTrack(_index, product as any, 'PointsMallProductValidMkt_Exp');
              }}
              sendTrack={(_index, product, sdEventName) => {
                sendTrack(_index, product as any, sdEventName);
              }}
            />
          )}
        </Box>
      ));
    }
    if (!listLoading) {
      return (
        <Box
          sx={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            py: 8,
          }}
        >
          <div className={`text-16 font-br text-[${colors.secondaryText}]`}>{t('noProducts')}</div>
        </Box>
      );
    }
  };

  const onPageChange = async (value: number) => {
    setPage(value);
  };

  useEffect(() => {
    if (serverList.length > 0) {
      setServerList([]);
      return;
    }
    getRedeemableList();
  }, [sortValue, page, activeTab]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      onTabChange(tab as string);
    }
  }, []);

  const PointsMallPageBtn_Click = (key: string) => {
    const btnName = tabItems.find((item) => item.key === key)?.label;

    trackEvent(
      {
        eventName: 'PointsMallPageBtn_Click',
        properties: {
          btn_name: btnName,
        },
      },
      [ProviderType.SENSORS],
    );
  };
  const renderSort = () => {
    if (deviceType === EPlatform.WAP) {
      return null;
    }
    return (
      <SortMenu
        options={sortOptions}
        value={sortValue}
        onChange={(value) => {
          if (value === sortValue) {
            return;
          }
          setSortValue(value as string);
          setPage(1);
          setRedeemableList([]);
          setHandleChangeSort(true);
        }}
      />
    );
  };
  const onTabChange = (key: string) => {
    // 更新URL但不触发导航
    const url = new URL(window.location.href);
    if (key === '0') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', key);
    }
    window.history.replaceState({}, '', url);

    setPage(1);
    setActiveTab(key);
    setRedeemableList([]);
    setSortValue('default');
  };
  useEffect(() => {
    scrollTopList();
  }, [redeemableList]);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const scrollTopList = useCallback(() => {
    if (
      deviceType === EPlatform.WAP &&
      page === 1 &&
      (handleChangeTab || handleChangeSort) &&
      redeemableList.length > 0
    ) {
      // 使用 requestAnimationFrame 确保在下一帧渲染完成后执行
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
      requestAnimationFrame(() => {
        if (tabsContainerRef.current) {
          // 获取元素相对于视口的位置
          const rect = tabsContainerRef.current.getBoundingClientRect();
          // 计算元素距离文档顶部的实际距离
          const scrollTop = rect.top + window.scrollY;
          // 35.5是redeem for freeshipping的距离
          // 43是网站顶栏header的距离
          // 42.5是tabs的距离
          window.scrollTo({
            top: scrollTop - 35.5 - 43 - 42.5,
            behavior: 'smooth',
          });
        }
        setHandleChangeTab(false);
        setHandleChangeSort(false);
      });
    }
  }, [redeemableList, deviceType, page, handleChangeTab, handleChangeSort]);

  return (
    <>
      <Box
        ref={tabsContainerRef}
        className="tabs-container"
        sx={{
          position: 'sticky',
          top: deviceType === EPlatform.WEB ? 0 : '78px',
          zIndex: 100,
          backgroundColor: 'white',
          width: '100%',
          left: 0,
          right: 0,
          '& .MuiTabs-root': {
            margin: deviceType === EPlatform.WAP ? '0 auto' : 0,
          },
        }}
      >
        <Tabs
          items={tabItems}
          activeKey={activeTab}
          onChange={(key) => {
            setHandleChangeTab(true);
            onTabChange(key);
            PointsMallPageBtn_Click(key);
          }}
          showLine
          hasTabContent={false}
          sx={{
            '& .MuiTabs-flexContainer': {
              justifyContent: 'center',
            },
            '& .MuiTab-root': {
              color: colors.auxiliaryDarkGray,
            },
            '& .Mui-selected': {
              color: `${colors.primaryText} !important`,
              fontFamily: font.bb,
            },
            '& .MuiTabs-list': {
              gap: deviceType === EPlatform.WEB ? 7 : 5,
            },
          }}
          tabBarExtraContent={renderSort()}
          tabBarExtraContentClassName="ml-auto"
        />
        <WapOnly>
          <div className="w-full h-44px flex items-center justify-end pr-14">
            <SortMenu
              options={sortOptions}
              value={sortValue}
              onChange={(value) => {
                if (value === sortValue) {
                  return;
                }
                setSortValue(value as string);
                setPage(1);
                setRedeemableList([]);
                setHandleChangeSort(true);
              }}
            />
          </div>
        </WapOnly>
      </Box>
      {/* 商品列表 */}
      <Box
        sx={{
          width: deviceType === EPlatform.WEB ? 'w-full' : '100vw',
          display: 'grid',
          mt: deviceType === EPlatform.WEB ? 3 : 0.5,
          gridTemplateColumns:
            deviceType === EPlatform.WEB
              ? {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(2, 1fr)',
                  xl: 'repeat(3, 1fr)',
                  '2xl': 'repeat(4, 1fr)',
                }
              : 'repeat(2, 1fr)',
          columnGap: deviceType === EPlatform.WEB ? { xs: 1, sm: 2 } : 0.5,
          rowGap: deviceType === EPlatform.WEB ? { xs: 10, sm: 8 } : 5,
          pb: deviceType === EPlatform.WEB ? 5 : 0,
        }}
        className={className}
      >
        {renderProductList()}
      </Box>
      {/* 展示全部时，不展示分页 */}
      <>
        <WebOnly>
          {total > pageLimit && (
            <Pagination
              className="mt-30"
              total={total}
              current={page}
              pageSize={pageLimit}
              onChange={(event, value) => onPageChange(value)}
            />
          )}
        </WebOnly>
        <WapOnly>
          <InfiniteScroll
            loading={listLoading}
            page={page}
            total={total}
            limit={pageLimit}
            onReachBottom={() => {
              setPage(page + 1);
            }}
          />
        </WapOnly>
      </>
    </>
  );
};

export default RedeemableItemsList;
